// pages/Documents/AddDocuments.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ListDocuments from './ListDocuments';
import { Modal as MuiModal, Box, Tabs, Tab, Button as MuiButton } from '@mui/material'; 
import backendURL from "../../config";
import { Document, Packer, Paragraph } from "docx";

// --- Tiptap Imports ---
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
// --- End Tiptap Imports ---

// --- Ant Design Imports ---
import { Button as AntButton, Modal as AntModal, Input, Space, Select, Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';
// --- End Ant Design Imports ---

const { Option } = Select;

const ensureUrlProtocol = (url) => { 
  if (!url) return '';
  if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};

const muiModalStyle = { 
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '80%', md: '900px' }, maxWidth: '90vw', maxHeight: '90vh',
  bgcolor: 'background.paper', border: 'none', borderRadius: '8px', boxShadow: 24,
  p: 0, display: 'flex', flexDirection: 'column', outline: 'none', 
};

const TiptapToolbar = ({ editor }) => { 
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run(); return;
    }
    const validatedUrl = ensureUrlProtocol(url);
    editor.chain().focus().extendMarkRange('link').setLink({ href: validatedUrl }).run();
  }, [editor]);

  const insertTableDialog = useCallback(() => setIsTableDialogOpen(true), []);
  const closeTableDialog = useCallback(() => setIsTableDialogOpen(false), []);

  const handleInsertTable = useCallback(() => {
    if (!editor) return;
    const rows = parseInt(tableRows, 10);
    const cols = parseInt(tableColumns, 10);
    if (rows > 0 && cols > 0) {
      editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    }
    setIsTableDialogOpen(false);
  }, [editor, tableRows, tableColumns]);

  const handleTableRowsChange = useCallback((e) => setTableRows(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
  const handleTableColumnsChange = useCallback((e) => setTableColumns(Math.max(1, parseInt(e.target.value, 10) || 1)), []);

  const handleAddColumn = useCallback(() => editor?.chain().focus().addColumnAfter().run(), [editor]);
  const handleAddRow = useCallback(() => editor?.chain().focus().addRowAfter().run(), [editor]);
  const handleDeleteColumn = useCallback(() => editor?.chain().focus().deleteColumn().run(), [editor]);
  const handleDeleteRow = useCallback(() => editor?.chain().focus().deleteRow().run(), [editor]);
  const handleDeleteTable = useCallback(() => editor?.chain().focus().deleteTable().run(), [editor]);

  const handleHeadingChange = useCallback((value) => {
    if (!editor) return;
    if (value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else {
      const level = parseInt(value.slice(1), 10);
      if (level >= 1 && level <= 6) {
        editor.chain().focus().toggleHeading({ level }).run();
      }
    }
  }, [editor]);

  if (!editor) {
    return <div className="p-2 border-bottom bg-light text-muted fst-italic">Editor loading...</div>;
  }

  const tableMenu = (
    <Menu>
      <Menu.Item key="insertTable" onClick={insertTableDialog}>Insert Table</Menu.Item>
      <Menu.Item key="addColumn" onClick={handleAddColumn} disabled={!editor.can().addColumnAfter()}>Add Column After</Menu.Item>
      <Menu.Item key="addRow" onClick={handleAddRow} disabled={!editor.can().addRowAfter()}>Add Row After</Menu.Item>
      <Menu.Item key="deleteColumn" onClick={handleDeleteColumn} disabled={!editor.can().deleteColumn()}>Delete Column</Menu.Item>
      <Menu.Item key="deleteRow" onClick={handleDeleteRow} disabled={!editor.can().deleteRow()}>Delete Row</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="deleteTable" onClick={handleDeleteTable} disabled={!editor.can().deleteTable()}>Delete Table</Menu.Item>
    </Menu>
  );

  const currentHeadingLevel = () => {
    for (let i = 1; i <= 6; i++) {
      if (editor.isActive('heading', { level: i })) return `h${i}`;
    }
    return 'paragraph';
  };

  return (
    <>
      <div className="tiptap-toolbar p-2 border-bottom bg-light sticky-top d-flex align-items-center flex-wrap">
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleBold().run()} disabled={!editor.can().chain().focus().toggleBold().run()} type={editor.isActive('bold') ? 'primary' : 'default'} title="Bold"><i className="fa fa-bold"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleItalic().run()} disabled={!editor.can().chain().focus().toggleItalic().run()} type={editor.isActive('italic') ? 'primary' : 'default'} title="Italic"><i className="fa fa-italic"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleUnderline().run()} disabled={!editor.can().chain().focus().toggleUnderline().run()} type={editor.isActive('underline') ? 'primary' : 'default'} title="Underline"><i className="fa fa-underline"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleStrike().run()} disabled={!editor.can().chain().focus().toggleStrike().run()} type={editor.isActive('strike') ? 'primary' : 'default'} title="Strike Through"><i className="fa fa-strikethrough"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={setLink} type={editor.isActive('link') ? 'primary' : 'default'} title="Add/Edit Link"> <i className="fa fa-link"></i> </AntButton>
        <Select size="small" value={currentHeadingLevel()} style={{ width: 120, margin: '0 8px' , zIndex: 1400 }} onChange={handleHeadingChange} disabled={!editor.can().setParagraph() && !editor.can().toggleHeading({level: 1})}>
          <Option value="paragraph">Paragraph</Option>
          <Option value="h1">Heading 1</Option><Option value="h2">Heading 2</Option><Option value="h3">Heading 3</Option>
          <Option value="h4">Heading 4</Option><Option value="h5">Heading 5</Option><Option value="h6">Heading 6</Option>
        </Select>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleBulletList().run()} type={editor.isActive('bulletList') ? 'primary' : 'default'} title="Bullet List"><i className="fa fa-list-ul"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleOrderedList().run()} type={editor.isActive('orderedList') ? 'primary' : 'default'} title="Ordered List"><i className="fa fa-list-ol"></i></AntButton>
        <Dropdown overlay={tableMenu} trigger={['click']} overlayStyle={{ zIndex: 1500 }}>
            <AntButton size="small" className="me-1"><span><i className="fa fa-table" style={{ marginRight: '4px'}}></i> Table <DownOutlined /></span></AntButton>
        </Dropdown>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><i className="fa fa-undo"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><i className="fa fa-repeat"></i></AntButton>
      </div>
      <AntModal title="Insert Table" open={isTableDialogOpen} onOk={handleInsertTable} onCancel={closeTableDialog} okText="Insert" cancelText="Cancel" zIndex={1500}>
        <Space direction="vertical" style={{ width: '100%' }}><Input type="number" addonBefore="Rows" value={tableRows} onChange={handleTableRowsChange} min={1} /><Input type="number" addonBefore="Columns" value={tableColumns} onChange={handleTableColumnsChange} min={1} /></Space>
      </AntModal>
    </>
  );
};


export default function AddDocuments() {
  useEffect(() => {
    document.title = 'Upload/Create Document | KlonIT Web Application';
  }, []);

  const [selectedFiles, setSelectedFiles] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false); // For file upload spinner

  // State for the "Create New Document" Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFilename, setNewFilename] = useState(''); // Used by both modal and potentially file upload if we want a filename override
  const [modalWebsiteLink, setModalWebsiteLink] = useState(''); // Website link for modal-created docs
  const [createLoading, setCreateLoading] = useState(false); // For modal save spinner
  const [createError, setCreateError] = useState(null); // For modal errors

  // State for the main file upload section's optional website link
  const [uploadWebsiteLink, setUploadWebsiteLink] = useState('');


  const editor = useEditor({
    extensions: [ StarterKit, Underline, Table.configure({ resizable: true }), TableCell, TableHeader, TableRow,
      Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
    ],
    content: '',
    editorProps: { attributes: { class: 'tiptap-editor-area p-3 form-control' } },
  });

  const handleFileChange = (event) => { 
    setSelectedFiles(event.target.files); setError(null); 
  };

  const handleFileUpload = async () => {
    if (!selectedFiles) { setError("Please select file(s) to upload."); return; }
    const formData = new FormData();
    let hasInvalidFile = false;

    for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const allowedExtensions = ['.docx', '.pdf', '.pptx', '.json', '.xml', '.txt', '.md', '.xlsx']; 
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
        if (!allowedExtensions.includes(fileExtension)) {
            setError(`Unsupported file type: ${file.name}. Allowed: ${allowedExtensions.join(', ')}.`);
            const fileInput = document.getElementById('input-20');
            if (fileInput) fileInput.value = '';
            setSelectedFiles(null);
            hasInvalidFile = true;
            break; 
        }
        formData.append("files", file);
    }

    if (hasInvalidFile) return;

    // Add optional website link for file uploads
    if (uploadWebsiteLink.trim()) {
        if (!/^(https?:\/\/)/i.test(uploadWebsiteLink.trim())) {
             setError("Invalid Website Link for upload. Must start with http:// or https://");
             return;
        }
        formData.append("website_link", uploadWebsiteLink.trim());
    }
    
    const token = localStorage.getItem('access_token'); if (!token) { setError("Authentication token not found."); return; }
    setLoading(true); setError(null); // setLoading for file upload
    try {
      const response = await axios.post(`${backendURL}/upload-private-data`, formData, { headers: { Authorization: `Bearer ${token}` }});
      setError(response.data.message || "Files uploaded successfully!");
      setSelectedFiles(null); 
      setUploadWebsiteLink(''); // Clear link after successful upload
      const fileInput = document.getElementById('input-20'); if(fileInput) fileInput.value = '';
      setRefresh(prev => !prev);
    } catch (err) {
      if (err.response) { setError(`Upload failed: ${err.response.data.detail || err.response.data.message || 'Server error'}`); }
      else if (err.request) { setError("Upload failed: No response from server."); } else { setError(`Upload failed: ${err.message}`); }
    } finally { 
      setLoading(false); 
    } 
  };

  const handleChange = (event, newValue) => { setActiveTab(newValue); };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setNewFilename('');
    setModalWebsiteLink(''); // Reset modal website link
    setCreateError(null);
    if (editor) {
        editor.commands.setContent('<p></p>');
    }
  };

  const handleCloseModal = () => {
    if (createLoading) return;
    setIsModalOpen(false);
  };
 
  const handleCreateDocument = async () => { // This is for DOCX creation from Tiptap
    setCreateLoading(true); // setCreateLoading for modal
    setCreateError(null);
    const token = localStorage.getItem('access_token');
    if (!token) {
      setCreateError("Authentication token not found.");
      setCreateLoading(false);
      return;
    }

    const formData = new FormData();

    if (!editor) {
        setCreateError("Editor not ready.");
        setCreateLoading(false);
        return;
    }
    if (!newFilename.trim()) {
        setCreateError("Filename cannot be empty.");
        setCreateLoading(false);
        return;
    }
    const finalFilename = newFilename.trim().endsWith('.docx') ? newFilename.trim() : `${newFilename.trim()}.docx`;
    if (editor.isEmpty) {
        setCreateError("Content cannot be empty.");
        setCreateLoading(false);
        return;
    }
    
    // Add optional website link for modal-created docs
    if (modalWebsiteLink.trim()) {
        if (!/^(https?:\/\/)/i.test(modalWebsiteLink.trim())) {
             setCreateError("Invalid Website Link. Must start with http:// or https://");
             setCreateLoading(false);
             return;
        }
        formData.append("website_link", modalWebsiteLink.trim());
    }
    
    const textContent = editor.getText({ blockSeparator: '\n\n' });
    const paragraphs = textContent.split('\n\n').map(text => new Paragraph(text.trim()));
    if (paragraphs.length === 0) paragraphs.push(new Paragraph(""));
    
    const doc = new Document({ sections: [{ properties: {}, children: paragraphs }] });
    try {
        const blob = await Packer.toBlob(doc);
        const docxFile = new File([blob], finalFilename, { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
        formData.append("files", docxFile, finalFilename); 
    } catch (packError) {
        console.error("Error packing DOCX:", packError);
        setCreateError("Failed to generate DOCX file from content.");
        setCreateLoading(false);
        return;
    }

    try {
      const response = await axios.post(
        `${backendURL}/upload-private-data`,
        formData, 
        {
          headers: { Authorization: `Bearer ${token}` } 
        }
      );

      setError(response.data.message || `Document "${finalFilename}" created successfully!`);
      handleCloseModal();
      setRefresh(prev => !prev);

    } catch (err) {
      console.error("Error in handleCreateDocument (DOCX):", err);
      if (err.response) {
        setCreateError(`Creation failed: ${err.response.data.detail || err.response.data.message || 'Server error'}`);
      } else if (err.request) {
        setCreateError("Creation failed: No response from server.");
      } else {
        setCreateError(`Creation failed: ${err.message}`);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div>
      <main className="content">
        <div className="container-fluid p-0">
          <div className="mb-3">
            <h1 className="h3 d-inline align-middle"><strong>Manage</strong> Documents</h1>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="card">
                 <div className="card-header"><h5 className="card-title mb-0">Upload or Create Document</h5></div>
                 <div className="card-body">
                  {/* File Upload Section - Centered */}
                  <div className="d-flex justify-content-center"> {/* NEW: Centering wrapper */}
                    <div style={{ maxWidth: '500px', width: '100%' }}> {/* NEW: Max width and full width inside flex */}
                      <div className="mb-3"> 
                        <label htmlFor="input-20" className="form-label text-center w-100"> {/* Added text-center w-100 */}
                          Upload Files (.docx, .pdf, .xlsx, .pptx, .json, .xml, .txt, .md)
                        </label>
                        <div className="input-group mb-2">
                          <input className="form-control" id="input-20" type="file" multiple 
                            accept=".docx,.pdf,.pptx,.json,.xml,.txt,.md,.xlsx"
                            onChange={handleFileChange} disabled={loading} />
                        </div>
                        <div className="mb-3"> {/* Optional Website Link for Upload */}
                            <label htmlFor="uploadWebsiteLink" className="form-label">Document Source (optional): </label>
                            <input
                                type="url"
                                className="form-control form-control-sm"
                                id="uploadWebsiteLink"
                                value={uploadWebsiteLink}
                                onChange={(e) => setUploadWebsiteLink(e.target.value)}
                                placeholder="https://example.com/source-of-document"
                                disabled={loading}
                            />
                        </div>
                        <div className="d-grid"> {/* Used d-grid to make button span full width */}
                            <button className="btn btn-primary" onClick={handleFileUpload} disabled={loading || !selectedFiles}>
                                {loading ? (<><span className="spinner-border spinner-border-sm me-1"></span> Uploading...</>) : ("Upload File(s)")}
                            </button>
                        </div>
                      </div>
                    </div> {/* End of max-width wrapper */}
                  </div> {/* End of centering wrapper */}
                    
                  <div className="text-center my-3"><span className="text-muted">─────── OR ───────</span></div>
                  
                  {/* Create Manually Button - Already centered by text-center on parent */}
                  <div className="text-center">
                      <MuiButton variant="contained" color="secondary" onClick={handleOpenModal}>
                        Create New Document (Text Editor)
                      </MuiButton>
                  </div>
                </div> {/* End card-body */}
                 {error && ( <div className={`alert ${error.toLowerCase().includes('success') || error.toLowerCase().includes('submitted') ? 'alert-success' : 'alert-warning'} m-3`} role="alert">{error}</div> )}
               </div> {/* End card */}
             </div>
           </div>
           <Tabs value={activeTab} onChange={handleChange} aria-label="document tabs" sx={{ marginTop: 2, borderBottom: 1, borderColor: 'divider' }}>
             <Tab label="List Documents" />
           </Tabs>
           <Box sx={{ paddingTop: 2 }}>
               {activeTab === 0 && <ListDocuments refresh={refresh} />}
           </Box>
        </div>
      </main>

    {/* Modal for "Create New Document Manually" (Tiptap editor) */}
    <MuiModal open={isModalOpen} onClose={handleCloseModal} aria-labelledby="create-doc-modal-title">
        <Box sx={muiModalStyle}>
            <div className="modal-content" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: 'none', height: '100%' }}>
              <div className="modal-header py-2 px-3">
                <h5 className="modal-title" id="create-doc-modal-title">Create New Document (.docx)</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={handleCloseModal} disabled={createLoading}></button>
              </div>

              {editor && <TiptapToolbar editor={editor} /> }

              <div className="modal-body p-3" style={{ flexGrow: 1, overflowY: 'auto' }}>
                {createError && 
                  !(createError.toLowerCase().includes('filename') || 
                    createError.toLowerCase().includes('link') ||
                    (editor?.isEmpty && createError.toLowerCase().includes('content'))) && 
                  ( <div className="alert alert-danger mb-2 py-1 px-2" role="alert" style={{fontSize: '0.85rem'}}>{createError}</div> )
                }
                
                <div className="mb-3">
                  <label htmlFor="filenameModal" className="form-label">Filename <span className="text-danger">*</span> <span className="text-muted fw-normal">(.docx will be added)</span></label>
                  <input type="text"
                    className={`form-control form-control-sm ${createError && createError.toLowerCase().includes('filename') ? 'is-invalid' : ''}`}
                    id="filenameModal" value={newFilename} onChange={(e) => setNewFilename(e.target.value)}
                    disabled={createLoading} placeholder="Enter document name..." autoFocus
                  />
                  {createError && createError.toLowerCase().includes('filename') && ( <div className="invalid-feedback">{createError}</div> )}
                </div>

                {/* Optional Website Link for Modal */}
                <div className="mb-3">
                    <label htmlFor="modalWebsiteLink" className="form-label">Optional: Source Website Link</label>
                    <input
                        type="url"
                        className={`form-control form-control-sm ${createError && createError.toLowerCase().includes('link') ? 'is-invalid' : ''}`}
                        id="modalWebsiteLink"
                        value={modalWebsiteLink}
                        onChange={(e) => setModalWebsiteLink(e.target.value)}
                        disabled={createLoading}
                        placeholder="https://example.com/related-to-this-doc"
                    />
                    {createError && createError.toLowerCase().includes('link') && ( <div className="invalid-feedback">{createError}</div> )}
                </div>

                {editor && (
                  <>
                    <div className="mb-1">
                      <label htmlFor="tiptap-content" className="form-label">Document Content <span className="text-danger">*</span></label>
                      <div className={`${createError && editor?.isEmpty && createError.toLowerCase().includes('content') ? 'tiptap-invalid' : ''}`}>
                        <EditorContent editor={editor} id="tiptap-content" />
                      </div>
                      {createError && editor?.isEmpty && createError.toLowerCase().includes('content') && ( <div className="invalid-feedback d-block">{createError}</div> )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer py-2 px-3">
                <button type="button" className="btn btn-sm btn-secondary" onClick={handleCloseModal} disabled={createLoading}>Cancel</button>
                <button type="button" className="btn btn-sm btn-primary" onClick={handleCreateDocument}
                  disabled={createLoading || !newFilename.trim() || (editor && editor.isEmpty)}>
                  {createLoading ? (<><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>) : "Save Document"}
                </button>
              </div>
            </div>
        </Box>
      </MuiModal>

       <style jsx global>{`
          .tiptap-editor-area { 
            min-height: 200px;
            max-height: calc(90vh - 350px);
            overflow-y: auto; border: 1px solid #ced4da; border-radius: 0.25rem; background-color: #fff;
          }
          .tiptap-editor-area:focus-within, .tiptap-editor-area.ProseMirror-focused {
             border-color: #86b7fe; outline: 0; box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
          }
          .tiptap-toolbar { flex-shrink: 0; z-index: 10; background-color: #f8f9fa; border-bottom: 1px solid #dee2e6; }
          .tiptap-invalid .tiptap-editor-area { border-color: #dc3545 !important; }
          .tiptap-invalid .tiptap-editor-area:focus-within, .tiptap-invalid .tiptap-editor-area.ProseMirror-focused {
              border-color: #dc3545 !important; box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
          }
          .ProseMirror { padding: 0.5rem; }
          .ProseMirror p { margin-bottom: 0.5rem; line-height: 1.5; }
          .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin-bottom: 0.5rem; }
          .ProseMirror li { margin-bottom: 0.1rem; } .ProseMirror li p { margin-bottom: 0.1rem; }
          .ProseMirror a { color: #0d6efd; text-decoration: underline; cursor: pointer; }
          .ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4, .ProseMirror h5, .ProseMirror h6 { margin-top: 0.75rem; margin-bottom: 0.3rem; line-height: 1.3; font-weight: 500; }
          .ProseMirror table { border-collapse: collapse; margin: 0.5rem 0; width: 100%; }
          .ProseMirror th, .ProseMirror td { border: 1px solid #dee2e6; padding: 0.3rem 0.4rem; vertical-align: top; }
          .ProseMirror th { font-weight: bold; text-align: left; background-color: #f1f3f5; }
          .tiptap-toolbar .ant-btn-sm { margin-bottom: 2px; margin-top: 2px; }
          .tiptap-toolbar .ant-select-sm { margin: 2px 8px 2px 8px !important; vertical-align: middle; }
          .ant-select-dropdown, .ant-dropdown-menu { z-index: 1350 !important; }
       `}</style>
    </div>
  );
}